import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '@medical/shared/contexts/AuthContext';
import { hasFeature } from '@medical/shared/utils/features';

const ButtonWrapper = styled.div`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 10000;
  pointer-events: none;
  display: block !important;
  visibility: visible !important;

  @media (max-width: 768px) {
    bottom: 1.5rem;
    right: 1.5rem;
  }
`;

const FloatingButton = styled(Link)`
  position: relative;
  background: #0B63C5;
  color: white;
  border: none;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
  text-decoration: none;
  font-size: 1.5rem;
  pointer-events: auto;
  z-index: 10000;
  display: flex !important;
  visibility: visible !important;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(102, 126, 234, 0.6);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    font-size: 1.25rem;
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: #ef4444;
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  border-radius: 50%;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
  padding: 0 4px;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
  }
`;

const FloatingNotificationButton = () => {
  const { user, token } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMedicationCount, setUnreadMedicationCount] = useState(0);

  const fetchUnreadCount = useCallback(async () => {
    if (!user || user.role !== 'patient' || !token) {
      setUnreadCount(0);
      setUnreadMedicationCount(0);
      return;
    }

    try {
      // Fetch both non-medication and medication unread counts
      const [nonMedResponse, medResponse] = await Promise.all([
        axios.get('/api/messages/unread-count', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }),
        axios.get('/api/messages/unread-medication-count', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
      ]);

      const nonMedCount = nonMedResponse.data.success ? (nonMedResponse.data.count || 0) : 0;
      const medCount = medResponse.data.success ? (medResponse.data.count || 0) : 0;

      console.log('Notification counts:', { nonMedCount, medCount, total: nonMedCount + medCount });

      setUnreadCount(nonMedCount);
      setUnreadMedicationCount(medCount);
    } catch (error) {
      console.error('Error fetching unread count:', error);
      // Set to 0 on error to ensure bell still shows
      setUnreadCount(0);
      setUnreadMedicationCount(0);
    }
  }, [user, token]);

  useEffect(() => {
    console.log('🔔 NotificationButton useEffect - user:', user ? { role: user.role, id: user.patientId || user._id } : 'null/undefined', 'token:', token ? 'exists' : 'missing');
    
    if (user && user.role === 'patient') {
      fetchUnreadCount();
      // Poll for updates every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      
      // Listen for refresh events when messages are read or Messages page is opened
      const handleRefresh = () => {
        // Small delay to ensure backend has processed the read status
        setTimeout(() => {
          fetchUnreadCount();
        }, 100);
      };
      window.addEventListener('refreshUnreadCount', handleRefresh);
      
      return () => {
        clearInterval(interval);
        window.removeEventListener('refreshUnreadCount', handleRefresh);
      };
    } else {
      // Reset count when user is not a patient
      setUnreadCount(0);
      setUnreadMedicationCount(0);
    }
  }, [user, token, fetchUnreadCount]);

  // Always show bell icon for patients, regardless of unread count
  // Don't return null during loading - wait for user to be determined
  if (user === undefined) {
    // Still loading, don't render yet
    return null;
  }
  
  if (!user || user.role !== 'patient') {
    return null;
  }

  if (!hasFeature(user, 'messaging') && !hasFeature(user, 'medicationReminders')) {
    return null;
  }

  // Total count includes both non-medication and medication notifications
  const totalUnreadCount = unreadCount + unreadMedicationCount;

  console.log('🔔 Bell Icon - Rendering with count:', totalUnreadCount, { 
    user: user ? { role: user.role, id: user.patientId || user._id } : null,
    unreadCount, 
    unreadMedicationCount,
    willShowBadge: totalUnreadCount > 0 
  });

  return (
    <ButtonWrapper>
      <FloatingButton 
        to="/messages" 
        title={totalUnreadCount > 0 ? `${totalUnreadCount} unread notification${totalUnreadCount > 1 ? 's' : ''} from Doctor` : 'Notifications from Doctor'}
        aria-label="Notifications"
      >
        🔔
        {totalUnreadCount > 0 ? (
          <NotificationBadge>{totalUnreadCount > 99 ? '99+' : totalUnreadCount}</NotificationBadge>
        ) : null}
      </FloatingButton>
    </ButtonWrapper>
  );
};

export default FloatingNotificationButton;

